<script lang="ts">
  import {
    login,
    signUp,
    verifySignupCode,
    resendSignupCode,
    consumeVerifyLink,
    devPreviewLogin,
    AuthError,
    NotAuthorizedError,
  } from '../lib/auth';

  let { notAuthorized = false }: { notAuthorized?: boolean } = $props();
  const isDev = import.meta.env.DEV;

  let email = $state('');
  let password = $state('');
  let submitting = $state(false);
  let error = $state<string | null>(null);
  // Opened from the sign-up email's link: start on the code step.
  const fromEmailLink = consumeVerifyLink();

  let code = $state('');
  let resending = $state(false);
  let mode = $state<'login' | 'signup' | 'verify'>(fromEmailLink ? 'verify' : 'login');
  let notice = $state<string | null>(
    fromEmailLink ? 'Kirjoita sähköpostiosoitteesi ja viestissä oleva koodi.' : null,
  );

  function switchMode(next: 'login' | 'signup' | 'verify'): void {
    mode = next;
    error = null;
    notice = null;
    code = '';
  }

  async function handleResend() {
    if (resending || !email) return;
    error = null;
    notice = null;
    resending = true;
    try {
      await resendSignupCode(email.trim());
      notice = 'Uusi koodi lähetetty. Tarkista sähköpostisi.';
    } catch (err) {
      error = err instanceof Error ? err.message : 'Koodin lähetys epäonnistui.';
    } finally {
      resending = false;
    }
  }

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (submitting) return;
    error = null;
    notice = null;
    submitting = true;
    try {
      if (mode === 'verify') {
        await verifySignupCode(email.trim(), code.replace(/\s/g, ''));
        code = '';
        // AuthGate observes auth state via onAuthChange and re-renders.
        return;
      }
      if (mode === 'signup') {
        const result = await signUp(email.trim(), password);
        password = '';
        if (result === 'confirm-email') {
          mode = 'verify';
          notice = 'Lähetimme sähköpostiisi vahvistuskoodin. Kirjoita se alle.';
        } else if (result === 'already-registered') {
          mode = 'login';
          notice = 'Tällä sähköpostilla on jo tunnus. Kirjaudu sisään.';
        }
        // 'signed-in': AuthGate observes auth state and re-renders.
        return;
      }
      await login(email.trim(), password);
      // Don't keep the password in memory after a successful login.
      password = '';
      // AuthGate observes auth state via onAuthChange and re-renders.
    } catch (err) {
      if (err instanceof AuthError && err.code === 'email_not_confirmed') {
        // An account created earlier but never confirmed: finish it here.
        password = '';
        mode = 'verify';
        code = '';
        notice = 'Sähköpostiosoitetta ei ole vielä vahvistettu. Kirjoita sähköpostin koodi alle tai pyydä uusi koodi.';
      } else if (err instanceof NotAuthorizedError) {
        error = 'Olet kirjautunut, mutta tällä tilillä ei ole pääsyä sovellukseen.';
      } else if (err instanceof AuthError) {
        error = err.message;
      } else if (err instanceof Error) {
        error = err.message;
      } else {
        error = 'Kirjautuminen epäonnistui. Yritä uudelleen.';
      }
    } finally {
      submitting = false;
    }
  }
</script>

<main>
  <div class="card">
    <div class="logo" aria-hidden="true">
      <svg viewBox="0 0 64 64">
        <circle class="ring-bg" cx="32" cy="32" r="22" />
        <circle class="ring" cx="32" cy="32" r="22" transform="rotate(-90 32 32)" />
        <text x="32" y="40" text-anchor="middle">5</text>
      </svg>
    </div>
    <h1>Viisi ateriaa</h1>
    <p class="lede">
      {mode === 'signup' ? 'Luo tunnus jatkaaksesi' : mode === 'verify' ? 'Vahvista sähköpostiosoitteesi' : 'Kirjaudu sisään jatkaaksesi'}
    </p>

    {#if notice}
      <div class="notice" role="status">{notice}</div>
    {/if}

    {#if notAuthorized}
      <div class="banner" role="alert">
        Tällä tilillä ei ole pääsyä sovellukseen. Kirjaudu talouden omalla tilillä.
      </div>
    {/if}

    <form onsubmit={handleSubmit} novalidate>
      <label class="field">
        <span class="label">Sähköposti</span>
        <input
          type="email"
          autocomplete="email"
          required
          bind:value={email}
          disabled={submitting}
          spellcheck="false"
          autocapitalize="none"
        />
      </label>

      {#if mode === 'verify'}
        <label class="field">
          <span class="label">Vahvistuskoodi</span>
          <input
            class="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="10"
            required
            bind:value={code}
            disabled={submitting}
          />
        </label>
      {:else}
        <label class="field">
          <span class="label">Salasana</span>
          <input
            type="password"
            autocomplete={mode === 'signup' ? 'new-password' : 'current-password'}
            required
            bind:value={password}
            disabled={submitting}
          />
        </label>
      {/if}

      {#if error}
        <div class="error" role="alert">{error}</div>
      {/if}

      <button
        type="submit"
        class="primary"
        disabled={submitting || !email || (mode === 'verify' ? code.trim().length < 6 : !password)}
      >
        {#if submitting}
          {mode === 'signup' ? 'Luodaan tunnusta...' : mode === 'verify' ? 'Vahvistetaan...' : 'Kirjaudutaan...'}
        {:else}
          {mode === 'signup' ? 'Luo tunnus' : mode === 'verify' ? 'Vahvista' : 'Kirjaudu sisään'}
        {/if}
      </button>
    </form>

    <p class="switch">
      {#if mode === 'verify'}
        Eikö koodia tullut?
        <button type="button" class="link" onclick={handleResend} disabled={resending || !email}>
          {resending ? 'Lähetetään...' : 'Lähetä uusi koodi'}
        </button>
        <br />
        <button type="button" class="link" onclick={() => switchMode('login')}>Takaisin kirjautumiseen</button>
      {:else if mode === 'signup'}
        Onko sinulla jo tunnus?
        <button type="button" class="link" onclick={() => switchMode('login')}>Kirjaudu sisään</button>
      {:else}
        Ensimmäinen kerta?
        <button type="button" class="link" onclick={() => switchMode('signup')}>Luo tunnus</button>
        <br />
        <button type="button" class="link" onclick={() => switchMode('verify')}>Sain koodin sähköpostiin</button>
      {/if}
    </p>

    {#if isDev}
      <div class="dev-section">
        <div class="dev-divider"><span>vain kehitys</span></div>
        <button type="button" class="ghost" onclick={() => devPreviewLogin()}>
          Ohita kirjautuminen ja esikatsele
        </button>
        <p class="dev-hint">
          Ohittaa oikean kirjautumisen, jotta näet sovelluksen ilman Supabase-asetuksia.
          Poistuu tuotantoversiosta.
        </p>
      </div>
    {/if}
  </div>
</main>

<style>
  main {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: var(--viisi-ground);
  }
  .card {
    background: var(--viisi-surface);
    border-radius: 20px;
    padding: 36px 28px;
    max-width: 380px;
    width: 100%;
    box-shadow: var(--viisi-shadow);
  }
  .logo {
    width: 64px;
    height: 64px;
    margin: 0 auto 10px;
  }
  .logo svg { width: 64px; height: 64px; display: block; }
  .ring-bg { fill: none; stroke: var(--viisi-surface-2); stroke-width: 6; }
  .ring {
    fill: none;
    stroke: var(--viisi-accent);
    stroke-width: 6;
    stroke-linecap: round;
    stroke-dasharray: 110 28;
  }
  .logo text {
    font-family: var(--viisi-font-display);
    font-weight: 800;
    font-size: 24px;
    fill: var(--viisi-ink);
  }
  h1 {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.01em;
    text-align: center;
    margin-bottom: 4px;
  }
  .lede {
    color: var(--viisi-ink-2);
    font-size: 14px;
    text-align: center;
    margin: 0 0 26px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .label {
    font-size: 13px;
    font-weight: 600;
    color: var(--viisi-ink-2);
  }
  input {
    width: 100%;
    padding: 11px 14px;
    font-size: 15px;
    border: 1px solid var(--viisi-line);
    border-radius: var(--viisi-radius-sm);
    background: var(--viisi-surface-2);
    color: var(--viisi-ink);
    transition: border-color 120ms ease, box-shadow 120ms ease;
  }
  input:focus {
    outline: none;
    border-color: var(--viisi-accent);
    box-shadow: 0 0 0 3px var(--viisi-accent-soft);
  }
  input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .error {
    background: var(--viisi-bad-soft);
    color: var(--viisi-bad);
    border-radius: var(--viisi-radius-sm);
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.4;
  }
  .banner {
    background: var(--viisi-warn-soft);
    color: var(--viisi-warn);
    border-radius: var(--viisi-radius-sm);
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.45;
    margin-bottom: 18px;
  }
  .notice {
    background: var(--viisi-accent-soft);
    color: var(--viisi-ink);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.45;
    margin-bottom: 14px;
  }
  .switch {
    margin: 18px 0 0;
    text-align: center;
    font-size: 13px;
    color: var(--viisi-ink-2);
  }
  .link {
    color: var(--viisi-accent);
    font-weight: 700;
    font-size: 13px;
    padding: 2px 4px;
  }
  .link:hover { text-decoration: underline; }
  .link:disabled { opacity: 0.5; cursor: not-allowed; }
  .code {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.3em;
    text-align: center;
  }
  .primary {
    margin-top: 4px;
    padding: 12px 16px;
    background: var(--viisi-accent);
    color: var(--viisi-accent-ink);
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    transition: transform 80ms ease, opacity 120ms ease;
  }
  .primary:active:not(:disabled) { transform: translateY(1px); }
  .primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .dev-section {
    margin-top: 24px;
  }
  .dev-divider {
    text-align: center;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--viisi-ink-3);
    margin: 16px 0 12px;
    position: relative;
  }
  .dev-divider::before,
  .dev-divider::after {
    content: '';
    position: absolute;
    top: 50%;
    width: calc(50% - 60px);
    height: 1px;
    background: var(--viisi-line);
  }
  .dev-divider::before { left: 0; }
  .dev-divider::after { right: 0; }
  .ghost {
    width: 100%;
    padding: 10px 14px;
    background: transparent;
    color: var(--viisi-ink-2);
    border: 1px dashed var(--viisi-line);
    border-radius: var(--viisi-radius-sm);
    font-size: 13px;
    font-weight: 600;
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  }
  .ghost:hover {
    background: var(--viisi-surface-2);
    border-color: var(--viisi-ink-3);
    color: var(--viisi-ink);
  }
  .dev-hint {
    margin: 8px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--viisi-ink-3);
    text-align: center;
  }
</style>
